/**
 * Thin wrapper around the browser Notification API.
 * SSR-safe — never crashes if window or Notification is undefined.
 */

export function isSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function isGranted(): boolean {
  return isSupported() && Notification.permission === 'granted';
}

export function isDenied(): boolean {
  return isSupported() && Notification.permission === 'denied';
}

/**
 * Request permission. Must be called from a user gesture handler.
 * Returns the resulting permission string.
 */
export async function requestPermission(): Promise<NotificationPermission> {
  if (!isSupported()) return 'denied';
  if (isGranted()) return 'granted';
  return Notification.requestPermission();
}

export interface SystemNotificationOptions {
  title: string;
  body?: string;
  icon?: string; // URL to icon
  tag?: string; // dedup tag — same tag replaces prior notification
  onClick?: () => void;
}

/**
 * Fires a system-level notification if permission is granted. No-op otherwise.
 * Returns the Notification instance (or null if not fired).
 */
export function showSystemNotification(
  opts: SystemNotificationOptions
): Notification | null {
  if (!isGranted()) return null;
  const notif = new Notification(opts.title, {
    body: opts.body,
    icon: opts.icon ?? '/favicon.svg',
    tag: opts.tag,
  });
  if (opts.onClick) {
    notif.onclick = () => {
      window.focus();
      opts.onClick!();
      notif.close();
    };
  }
  return notif;
}
